import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, Lightning, Star, Crown } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

// ============================================================
// Blue Lock Mode — LOCKED / COMING SOON page.
// Visible to all signed-in users but greyed-out with a clear
// "Locked · Coming Soon" badge. Keeps the JWFL roadmap visible
// without breaking the experience.
// ============================================================
export const BlueLockPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at 20% 20%, rgba(0,229,255,0.12) 0%, transparent 50%),' +
          'radial-gradient(ellipse at 80% 80%, rgba(255,0,110,0.10) 0%, transparent 50%),' +
          '#04060F',
      }}
      data-testid="blue-lock-page"
    >
      {/* Scanlines */}
      <div className="absolute inset-0 pointer-events-none opacity-25 mix-blend-overlay"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,229,255,0.05) 0 1px, transparent 1px 3px)' }} />

      <div className="max-w-4xl mx-auto relative z-10">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#7A86B5] hover:text-[#00E5FF] mb-8 transition-colors text-sm tracking-wide"
          data-testid="back-home-btn"
        >
          <ArrowLeft size={16} /> Back to home
        </button>

        {/* Hero card */}
        <div
          className="relative rounded-lg border overflow-hidden p-10 sm:p-14 text-center"
          style={{
            background: 'linear-gradient(180deg, rgba(10,13,31,0.95) 0%, rgba(4,6,15,0.95) 100%)',
            borderColor: 'rgba(122,134,181,0.25)',
            boxShadow: '0 0 80px rgba(0,229,255,0.08), inset 0 0 100px rgba(0,229,255,0.04)',
          }}
        >
          {/* Locked diagonal stripes */}
          <div className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg, #FFB400 0 12px, transparent 12px 26px)',
            }} />

          {/* Lock icon */}
          <div className="relative inline-flex items-center justify-center mb-6">
            <div className="absolute inset-0 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(0,229,255,0.30) 0%, transparent 70%)',
                filter: 'blur(20px)',
                width: '160px', height: '160px',
                left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              }} />
            <div className="relative w-28 h-28 rounded-full border-2 flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #0A0D1F 0%, #04060F 100%)',
                borderColor: '#00E5FF',
                boxShadow: '0 0 40px rgba(0,229,255,0.55), inset 0 0 20px rgba(0,229,255,0.15)',
              }}
            >
              <Lock size={48} weight="fill" style={{ color: '#00E5FF', filter: 'drop-shadow(0 0 8px rgba(0,229,255,0.8))' }} />
            </div>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full border"
            style={{
              borderColor: '#FFB400',
              background: 'rgba(255,180,0,0.08)',
              boxShadow: '0 0 16px rgba(255,180,0,0.25)',
            }}
            data-testid="locked-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB400] animate-pulse" />
            <span className="text-[10px] font-bold tracking-[0.32em] uppercase" style={{ color: '#FFB400' }}>
              LOCKED · COMING SOON
            </span>
          </div>

          {/* Title */}
          <h1 className="font-heading font-black tracking-wide text-5xl sm:text-7xl mb-3 ewfl-grad-text uppercase"
            data-testid="blue-lock-title">
            Blue Lock Mode
          </h1>
          <p className="text-[10px] uppercase tracking-[0.38em] mb-6"
            style={{ color: '#00E5FF', textShadow: '0 0 10px rgba(0,229,255,0.4)' }}>
            ブルーロック · 300 Strikers · 1 World Champion
          </p>

          <p className="text-[#7A86B5] text-base max-w-2xl mx-auto leading-relaxed mb-10">
            The most ambitious mode in JWFL. 300 elite Japanese strikers, every roster filled with 89–97 OVR finishers,
            a brutal knockout pyramid, and only <span className="text-white font-semibold">one</span> emerges as Japan's
            World-Cup-winning striker. <span className="text-[#FF006E] font-semibold">Currently in development.</span>
          </p>

          {/* Feature preview grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
            {[
              { icon: Star, label: 'ELITE 89-97 OVR', color: '#00E5FF' },
              { icon: Lightning, label: 'KNOCKOUT PYRAMID', color: '#FF006E' },
              { icon: Crown, label: 'ONE CHAMPION', color: '#CCFF00' },
            ].map((f, i) => (
              <div key={i}
                className="px-4 py-5 rounded-md border opacity-50 hover:opacity-75 transition-opacity"
                style={{ borderColor: 'rgba(122,134,181,0.20)', background: 'rgba(10,13,31,0.5)' }}
              >
                <f.icon size={22} weight="fill" style={{ color: f.color }} className="mx-auto mb-2" />
                <p className="font-heading font-bold text-xs tracking-[0.18em] uppercase text-white/85">{f.label}</p>
              </div>
            ))}
          </div>

          <Button
            disabled
            className="bg-[#0A0D1F] border border-[#1E2240] text-[#7A86B5] cursor-not-allowed font-heading uppercase tracking-[0.18em] text-sm px-8 py-3 rounded-md"
            data-testid="unlock-btn-disabled"
          >
            <Lock size={14} className="mr-2" /> Unlock in a future update
          </Button>

          <p className="mt-8 text-[10px] uppercase tracking-[0.32em] text-[#4A5278]">
            Tracked on the JWFL roadmap · expected Season 3 patch
          </p>
        </div>
      </div>
    </div>
  );
};

export default BlueLockPage;
